'use client'

import React, { useEffect, useRef } from "react";
import { MediaState } from "@/types/types";
import useMediaState from "@/common/hooks/useMediaState";
import useAudioLoaded from "@/common/hooks/useAudioLoaded";
import { useSetCurrentExplorerItemBySearchParam } from "@/common/hooks/useSetCurrentExplorerItemBySearchParam";
import usePlaymodeSearchParam from "@/common/hooks/usePlaymodeSearchParam";
import useAudioEnded from "@/common/hooks/useAudioEnded";
import useApiAuthorizationCookie from "@/common/hooks/useApiAuthorizationCookie";
import useSessionUser from "@/common/hooks/useSessionUser";
import { AudioAdapter } from "@/features/AudioWorkstation/adapters/AudioAdapter";
import { useAtom } from "jotai";
import { audioAdapterAtom } from "@/state/jotai/atom/audioAdapterAtom";
import { getCookie } from "cookies-next";
import { API_AUTH_COOKIE_NAME } from "@/enum/cookie.name.enum.ts";

/**
 * AudioAdapterProvider is verantwoordelijk voor het creëren en beheren van de AudioAdapter,
 * die centraal wordt gebruikt voor audio afspelen in de applicatie.
 *
 * De provider:
 * 1. Maakt één AudioContext en één HTMLAudioElement aan voor hergebruik
 * 2. Initialiseert de AudioAdapter met deze gedeelde resources
 * 3. Stelt event handlers in voor media state updates
 * 4. Maakt de AudioAdapter beschikbaar via de Jotai state
 */
export default function AudioAdapterProvider(): React.ReactNode {
  const user = useSessionUser();
  const firstRender = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const [playmode] = usePlaymodeSearchParam();
  const jsonWebToken = getCookie(API_AUTH_COOKIE_NAME) as string;
  const [apiAuthorizationToken] = useApiAuthorizationCookie();
  const [audioAdapter, setAudioAdapter] = useAtom(audioAdapterAtom);
  const [, setMediaState] = useMediaState();
  const [, setAudioLoaded] = useAudioLoaded();

  // Hooks voor audio gedrag
  useAudioEnded();
  useSetCurrentExplorerItemBySearchParam();

  // Event handlers voor media state updates
  useEffect(() => {
    if (!audioAdapter || !audioElementRef.current) return;

    const handlePlay = () => setMediaState(MediaState.PLAYING);
    const handlePause = () => setMediaState(MediaState.PAUSED);
    const handleLoaded = () => setAudioLoaded(true);
    const handleUnloaded = () => setAudioLoaded(false);

    const audioElement = audioElementRef.current;
    if (!audioElement) return;

    // Voeg event listeners toe
    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('loadedmetadata', handleLoaded);
    audioElement.addEventListener('emptied', handleUnloaded);

    // Cleanup functie
    return () => {
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('loadedmetadata', handleLoaded);
      audioElement.removeEventListener('emptied', handleUnloaded);
    };
  }, [
    audioAdapter,
    setMediaState,
    setAudioLoaded
  ]);

  // Initialize audio context, element, and adapter
  useEffect(() => {
    // Voorkom dubbele initialisatie of initialisatie zonder user/auth token
    if (!user?.id || firstRender.current || audioAdapter || !apiAuthorizationToken) {
      return;
    }

    try {
      console.log('Creating AudioContext and AudioElement for AudioAdapter');

      // 1. Maak AudioContext voor Web Audio operaties
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();

      // 2. Maak Audio Element voor main playback
      audioElementRef.current = new Audio();
      audioElementRef.current.controls = true;
      audioElementRef.current.preload = "metadata";
      audioElementRef.current.crossOrigin = "anonymous";

      // Controleer of beide elementen aangemaakt zijn
      if (!audioContextRef.current || !audioElementRef.current) {
        throw new Error('Failed to create AudioContext or Audio element');
      }

      // 3. Maak AudioAdapter met de gedeelde resources
      const adapter = new AudioAdapter(
        audioElementRef.current,
        audioContextRef.current
      );

      // 4. Sla de adapter op in de Jotai state
      setAudioAdapter(adapter);
      firstRender.current = true;

      console.log('AudioAdapter initialized successfully');
    } catch (error) {
      console.error('Error initializing AudioAdapter:', error);
    }

    // Cleanup functie
    return () => {
      // Adapter destroy wordt afgehandeld door de Jotai atom
      // als de component unmounted wordt; hier niet nodig
    };
  }, [
    user,
    playmode,
    audioAdapter,
    setAudioAdapter,
    apiAuthorizationToken,
    jsonWebToken
  ]);

  // Provider rendert niets, het stelt alleen de adapter in
  return null;
}
